import React, { useEffect, useState } from "react";
import { ymd } from "../lib/dates";
import { Printer, ChartBar } from "@phosphor-icons/react";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import api, { formatIDR, formatDate } from "../lib/api";
import { generatePDF } from "../lib/pdf";

const NOMEN = [
  { code: "AJP", name: "Amal Jariyah Pengembangan" },
  { code: "IPP", name: "Infaq Penyelenggaraan Pendidikan" },
  { code: "DKG", name: "Dana Kegiatan" },
  { code: "DPR", name: "Dana Perlengkapan" },
  { code: "DBK", name: "Dana Buku" },
  { code: "DSR", name: "Dana Seragam" },
];

export default function LaporanKeuangan() {
  const today = ymd();
  const monthStart = today.slice(0, 8) + "01";
  const [start, setStart] = useState(monthStart);
  const [end, setEnd] = useState(today);
  const [lembagaId, setLembagaId] = useState("all");
  const [lembagas, setLembagas] = useState([]);
  const [summary, setSummary] = useState(null);
  const [settings, setSettings] = useState({});

  const load = async () => {
    const [l, sett] = await Promise.all([api.get("/lembaga"), api.get("/settings").catch(() => ({ data: {} }))]);
    setLembagas(l.data);
    setSettings(sett.data);
    const q = `?start_date=${start}&end_date=${end}` + (lembagaId !== "all" ? `&lembaga_id=${lembagaId}` : "");
    const r = await api.get(`/reports/summary${q}`);
    setSummary(r.data);
  };
  useEffect(() => { load(); }, [start, end, lembagaId]);

  const chartData = NOMEN.map((n) => ({
    name: n.code,
    Pemasukan: summary?.by_nomenklatur_income?.[n.code] || 0,
    Pengeluaran: summary?.by_nomenklatur_expense?.[n.code] || 0,
  }));

  const printPDF = () => {
    if (!summary) return;
    const rows = NOMEN.map((n) => {
      const inc = summary.by_nomenklatur_income?.[n.code] || 0;
      const exp = summary.by_nomenklatur_expense?.[n.code] || 0;
      return [n.code, n.name, formatIDR(inc), formatIDR(exp), formatIDR(inc - exp)];
    });
    generatePDF({
      title: "Laporan Keuangan",
      subtitle: `${formatDate(start)} — ${formatDate(end)}`,
      headers: ["Kode", "Nomenklatur", "Pemasukan", "Pengeluaran", "Net"],
      rows,
      meta: {
        "Total Pemasukan": formatIDR(summary.total_income),
        "Total Pengeluaran": formatIDR(summary.total_expense),
        "Cashflow (Net)": formatIDR(summary.cashflow),
      },
      filename: `laporan-keuangan-${Date.now()}.pdf`,
      settings,
    });
  };

  return (
    <div data-testid="laporan-keuangan-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-6 gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#0B8D95] font-semibold mb-1">Laporan</div>
          <h1 className="font-display text-3xl font-black text-gray-900">Laporan Keuangan</h1>
          <p className="text-gray-600 text-sm mt-1">Ringkasan pemasukan dan pengeluaran per nomenklatur.</p>
        </div>
        <Button variant="outline" onClick={printPDF} data-testid="laporan-print-button"><Printer size={16} className="mr-1.5" />Cetak PDF</Button>
      </div>

      <div className="bg-white border border-gray-200 rounded-md p-4 mb-6 flex flex-col sm:flex-row gap-3 items-end">
        <div><Label className="text-xs">Dari</Label><Input type="date" value={start} onChange={(e)=>setStart(e.target.value)} /></div>
        <div><Label className="text-xs">Sampai</Label><Input type="date" value={end} onChange={(e)=>setEnd(e.target.value)} /></div>
        <div className="sm:ml-auto">
          <Label className="text-xs">Lembaga</Label>
          <Select value={lembagaId} onValueChange={setLembagaId}>
            <SelectTrigger className="w-56 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">Semua Lembaga</SelectItem>
              {lembagas.map((l)=><SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-gray-200 rounded-md p-5">
          <div className="text-xs uppercase tracking-wider font-semibold text-gray-500">Total Pemasukan</div>
          <div className="font-mono text-2xl font-bold text-[#059669] mt-1">{formatIDR(summary?.total_income || 0)}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-md p-5">
          <div className="text-xs uppercase tracking-wider font-semibold text-gray-500">Total Pengeluaran</div>
          <div className="font-mono text-2xl font-bold text-[#DC2626] mt-1">{formatIDR(summary?.total_expense || 0)}</div>
        </div>
        <div className="bg-white border border-gray-200 rounded-md p-5">
          <div className="text-xs uppercase tracking-wider font-semibold text-gray-500">Cashflow (Net)</div>
          <div className={`font-mono text-2xl font-bold mt-1 ${(summary?.cashflow || 0) >= 0 ? "text-gray-900" : "text-[#DC2626]"}`}>{formatIDR(summary?.cashflow || 0)}</div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-md p-5 mb-6">
        <div className="font-display text-lg font-bold text-gray-900 mb-4">Perbandingan per Nomenklatur</div>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
              <Tooltip formatter={(v)=>formatIDR(v)} />
              <Legend />
              <Bar dataKey="Pemasukan" fill="#059669" radius={[4,4,0,0]} />
              <Bar dataKey="Pengeluaran" fill="#DC2626" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 font-display text-lg font-bold">Detail per Nomenklatur</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
              <tr><th className="px-5 py-3 font-semibold">Kode</th><th className="px-5 py-3 font-semibold">Nama</th><th className="px-5 py-3 font-semibold text-right">Pemasukan</th><th className="px-5 py-3 font-semibold text-right">Pengeluaran</th><th className="px-5 py-3 font-semibold text-right">Net</th></tr>
            </thead>
            <tbody>
              {NOMEN.map((n) => {
                const inc = summary?.by_nomenklatur_income?.[n.code] || 0;
                const exp = summary?.by_nomenklatur_expense?.[n.code] || 0;
                return (
                  <tr key={n.code} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-5 py-3 font-mono font-semibold text-[#0B8D95]">{n.code}</td>
                    <td className="px-5 py-3">{n.name}</td>
                    <td className="px-5 py-3 text-right font-mono text-[#059669]">{formatIDR(inc)}</td>
                    <td className="px-5 py-3 text-right font-mono text-[#DC2626]">{formatIDR(exp)}</td>
                    <td className={`px-5 py-3 text-right font-mono font-bold ${(inc - exp) >= 0 ? "text-gray-900" : "text-[#DC2626]"}`}>{formatIDR(inc - exp)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
